import { apiClient, authenticatedClient } from '../lib/client';
import { AUTH_ENDPOINTS } from './auth.utils';
import type { ApiResponse } from '../lib/types';
import type {
    LoginCredentials,
    SignupPayload,
    TokenResponse,
    UserProfile,
    PasswordResetPayload,
    ForgotPasswordPayload,
    ValidateInvitationPayload,
    SignupMemberPayload,
    SignupMemberResponse,
    SignupResponse,
    ConfirmEmailResponse,
    // 2FA Types
    TwoFASetupResponse,
    TwoFAVerifySetupRequest,
    TwoFAVerifySetupResponse,
    TwoFAVerifyLoginRequest,
    TwoFADisableRequest,
    TwoFADisableResponse,
    TwoFAStatusResponse,
    TwoFARegenerateBackupCodesResponse,
    LoginResponse,
} from './auth.types';
import { setAuthTokens, clearAuthTokens, getAuthTokens } from '../lib/config';

export const login = async (credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> => {
    try {
        // Convert credentials to URLSearchParams for x-www-form-urlencoded format
        const formData = new URLSearchParams();
        formData.append('username', credentials.email);
        formData.append('password', credentials.password);

        const response = await apiClient.post(AUTH_ENDPOINTS.LOGIN, formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });

        const loginResponse: LoginResponse = response.data;

        // If 2FA is NOT required, store tokens
        if (!loginResponse.requires_2fa && loginResponse.access_token && loginResponse.refresh_token) {
            setAuthTokens(loginResponse.access_token, loginResponse.refresh_token);
        }

        return { data: loginResponse };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Login failed' };
    }
};

export const signup = async (payload: SignupPayload): Promise<ApiResponse<SignupResponse>> => {
    try {
        const response = await apiClient.post(AUTH_ENDPOINTS.SIGNUP, payload);
        // console.log('Signup response:', response.data);
        const { message } = response.data;
        return { data: { message } };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Signup failed' };
    }
};

export const signupMember = async (payload: SignupMemberPayload): Promise<ApiResponse<SignupMemberResponse>> => {
    try {
        const response = await apiClient.post(AUTH_ENDPOINTS.SIGNUP_MEMBER, payload);
        // console.log('Signup member response:', response.data);
        const { access_token: access_token, refresh_token: refresh_token } = response.data;
        // console.log('Storing tokens:', { access_token, refresh_token });
        setAuthTokens(access_token, refresh_token);
        return { data: { access_token, refresh_token, message: response.data.message } };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Member signup failed' };
    }
};

export const logout = async (): Promise<ApiResponse> => {
    try {
        await authenticatedClient.post(AUTH_ENDPOINTS.LOGOUT);
        clearAuthTokens();
        return { message: 'Logged out successfully' };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Logout failed' };
    }
};

export const getCurrentUser = async (): Promise<ApiResponse<UserProfile>> => {
    try {
        const response = await authenticatedClient.get(AUTH_ENDPOINTS.ME);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch user profile' };
    }
};

export const refreshAuthToken = async (): Promise<ApiResponse<TokenResponse>> => {
    try {
        const tokens = getAuthTokens();
        if (!tokens?.refresh_token) {
            throw new Error('No refresh token available');
        }

        const response = await apiClient.post(AUTH_ENDPOINTS.REFRESH_TOKEN, {
            refresh_token: tokens.refresh_token,
        });

        // console.log('Refresh token response:', response.data);
        const { access_token: access_token, refresh_token: refresh_token } = response.data;
        // console.log('Storing tokens:', { access_token, refresh_token });
        setAuthTokens(access_token, refresh_token);
        return { data: { access_token, refresh_token } };
    } catch (error: any) {
        clearAuthTokens();
        return { error: error.response?.data?.message || 'Token refresh failed' };
    }
};

export const resetPassword = async (payload: PasswordResetPayload): Promise<ApiResponse> => {
    try {
        await apiClient.post(AUTH_ENDPOINTS.RESET_PASSWORD, payload);
        return { message: 'Password reset successful' };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Password reset failed' };
    }
};

export const forgotPassword = async (payload: ForgotPasswordPayload): Promise<ApiResponse> => {
    try {
        await apiClient.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, payload);
        return { message: 'Password reset instructions sent to email' };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to process forgot password request' };
    }
};

export const validateInvitation = async (payload: ValidateInvitationPayload): Promise<ApiResponse> => {
    try {
        const response = await apiClient.post(AUTH_ENDPOINTS.VALIDATE_INVITATION, payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Invalid invitation' };
    }
};

export const confirmEmail = async (token: string): Promise<ApiResponse<ConfirmEmailResponse>> => {
    try {
        const response = await apiClient.post(AUTH_ENDPOINTS.CONFIRM_EMAIL(token), { token });
        const { message, access_token: access_token, refresh_token: refresh_token } = response.data;
        setAuthTokens(access_token, refresh_token);
        return { data: { message, access_token, refresh_token } };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Email confirmation failed' };
    }
};

// ==================== 2FA API Functions ====================

/**
 * Initialize 2FA setup for the current user.
 * Generates a TOTP secret, QR code, and prepares for activation.
 * User must verify a code from their authenticator app before 2FA is activated.
 */
export const setup2FA = async (): Promise<ApiResponse<TwoFASetupResponse>> => {
    try {
        const response = await authenticatedClient.post(AUTH_ENDPOINTS.TWO_FA_SETUP);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to setup 2FA' };
    }
};

/**
 * Verify 2FA setup by checking a TOTP code from the user's authenticator app.
 * Upon successful verification, activates 2FA and returns backup recovery codes.
 */
export const verify2FASetup = async (payload: TwoFAVerifySetupRequest): Promise<ApiResponse<TwoFAVerifySetupResponse>> => {
    try {
        const response = await authenticatedClient.post(AUTH_ENDPOINTS.TWO_FA_VERIFY_SETUP, payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to verify 2FA setup' };
    }
};

/**
 * Verify 2FA code during login and return access tokens.
 * Accepts either TOTP codes or backup recovery codes.
 * This is called after initial login when 2FA is enabled.
 */
export const verify2FALogin = async (payload: TwoFAVerifyLoginRequest): Promise<ApiResponse<TokenResponse>> => {
    try {
        const response = await apiClient.post(AUTH_ENDPOINTS.TWO_FA_VERIFY_LOGIN, payload);
        const { access_token, refresh_token } = response.data;
        setAuthTokens(access_token, refresh_token);
        return { data: { access_token, refresh_token } };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to verify 2FA code' };
    }
};

/**
 * Disable 2FA for the current user.
 * Requires password confirmation for security.
 */
export const disable2FA = async (payload: TwoFADisableRequest): Promise<ApiResponse<TwoFADisableResponse>> => {
    try {
        const response = await authenticatedClient.post(AUTH_ENDPOINTS.TWO_FA_DISABLE, payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to disable 2FA' };
    }
};

/**
 * Get current 2FA status for the authenticated user.
 */
export const get2FAStatus = async (): Promise<ApiResponse<TwoFAStatusResponse>> => {
    try {
        const response = await authenticatedClient.get(AUTH_ENDPOINTS.TWO_FA_STATUS);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to get 2FA status' };
    }
};

/**
 * Generate new backup codes, replacing any existing ones.
 * Requires 2FA to be enabled and verified.
 */
export const regenerate2FABackupCodes = async (): Promise<ApiResponse<TwoFARegenerateBackupCodesResponse>> => {
    try {
        const response = await authenticatedClient.post(AUTH_ENDPOINTS.TWO_FA_REGENERATE_BACKUP_CODES);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to regenerate backup codes' };
    }
};
